import React, { useEffect, useRef, type ReactNode } from 'react';

interface StyleProviderProps {
  /**
   * A DOM element to clone matched styles into. Typically a `<div>` inside a
   * Shadow DOM.
   */
  container: HTMLElement;
  /**
   * The attribute name to match on `<style>` tags.
   * e.g. `"data-style-group"`
   */
  attribute: string;
  /** Children to render. */
  children: ReactNode;
  /**
   * Optional attribute value to match. When provided, only `<style>` tags
   * whose attribute equals this value are cloned.
   * When omitted, any `<style>` with the attribute present is cloned.
   */
  value?: string;
  /**
   * Where to look for `<style>` tags to clone.
   * @default document.head
   */
  source?: HTMLElement;
}

/**
 * StyleProvider
 *
 * Clones `<style>` tags from `document.head` into a container element
 * (typically inside a Shadow DOM) based on a configurable attribute selector.
 *
 * The source styles are expected to be disabled in the parent DOM via an
 * impossible media query (e.g. `media="max-width: 1px"`).  Cloned copies
 * have the `media` attribute removed so they are active only inside the
 * Shadow DOM.
 *
 * Multiple `StyleProvider` instances can share the same source styles —
 * each instance creates its own independent clones.
 *
 * ## How it works
 *
 * 1. Configure your bundler (e.g. webpack `style-loader`) to add a custom
 *    attribute and a disabling media query to injected `<style>` tags.
 * 2. Wrap your components with `<StyleProvider>`, passing the attribute name
 *    and a container ref inside the Shadow DOM.
 * 3. The provider clones matching `<style>` tags into the container with the
 *    `media` attribute removed, and watches for new ones via
 *    `MutationObserver`.
 *
 * ## Usage
 *
 * ```ts
 * // webpack.config.js
 * {
 *   test: /\.compiled\.css$/,
 *   use: [
 *     {
 *       loader: 'style-loader',
 *       options: {
 *         attributes: { 'data-style-group': 'atlaskit', media: 'max-width: 1px' },
 *       },
 *     },
 *     { loader: 'css-loader' },
 *   ],
 * }
 * ```
 *
 * ```tsx
 * // component.tsx
 * import { StyleProvider } from '@loomhq/lens';
 *
 * function ShadowContainer({ children }) {
 *   const [styleContainer, setStyleContainer] = useState<HTMLElement | null>(null);
 *
 *   return (
 *     <root.div>
 *       <div ref={setStyleContainer}>
 *         {styleContainer && (
 *           <StyleProvider container={styleContainer} attribute="data-style-group" value="atlaskit">
 *             {children}
 *           </StyleProvider>
 *         )}
 *       </div>
 *     </root.div>
 *   );
 * }
 * ```
 */
export function StyleProvider({
  container,
  attribute,
  value,
  children,
  source,
}: StyleProviderProps) {
  const clonedStyles = useRef<HTMLStyleElement[]>([]);

  useEffect(() => {
    if (!container || typeof document === 'undefined') {
      return;
    }

    const sourceEl = source ?? document.head;

    const matches = (node: Node): node is HTMLStyleElement => {
      if (!(node instanceof HTMLStyleElement)) {
        return false;
      }
      if (!node.hasAttribute(attribute)) {
        return false;
      }
      if (value !== undefined && node.getAttribute(attribute) !== value) {
        return false;
      }
      return true;
    };

    const cloneStyle = (style: HTMLStyleElement) => {
      const clone = style.cloneNode(true) as HTMLStyleElement;
      // Remove the disabling media query so the styles are active inside the Shadow DOM.
      clone.removeAttribute('media');
      container.appendChild(clone);
      clonedStyles.current.push(clone);
    };

    // Clone existing matching styles.
    sourceEl.querySelectorAll('style').forEach(style => {
      if (matches(style)) {
        cloneStyle(style);
      }
    });

    // Watch for new matching styles.
    // Clones are deferred via queueMicrotask to avoid reading the source
    // tree inside the MutationObserver callback before the browser has
    // finished attaching the stylesheet.
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (matches(node)) {
            queueMicrotask(() => cloneStyle(node));
          }
        }
      }
    });

    observer.observe(sourceEl, { childList: true });

    // Cleanup: disconnect observer and remove cloned styles.
    return () => {
      observer.disconnect();

      for (const clone of clonedStyles.current) {
        clone.remove();
      }
      clonedStyles.current = [];
    };
  }, [container, attribute, value, source]);

  return <>{children}</>;
}
